import { type AnyAction, type Dispatch, type Middleware, type MiddlewareAPI } from 'redux';
import { get } from 'lodash';
import { SET_CARD_FILTER, SET_DATA_ROOM_MODE, UPDATE_FILTER } from '../actions/actionTypes/filters';
import { EVENT_ARGS, GA_DIMENSIONS } from '../../tracker/config';
import { REQUEST_TYPES } from '../../api/requests/requestTypes';
import { filterIds, reportTypes, reportViews } from '../../utils/filters';
import { SEND_REQUEST_SUCCESS } from '../actions/actionTypes/request';
import { sendEvent } from '../../tracker/MatomoAnalytics';
import { type RootState } from '../types';

const _trackReportType = (reportType: string | undefined, report: any): void => {
    if (reportType === reportTypes.keywordGuidance) {
        sendEvent(...(EVENT_ARGS({ report }).reports.guidanceReportGenerated as [string, string, any?]));
    }
};

const _trackReportViewFilters = (filterValue: any, selectedValueCount: number, selectedSubValue: any): void => {
    const matomoTrackingObj = { filterBy: filterValue, filterValue: selectedSubValue };
    if (selectedValueCount <= 0) {
        return;
    }
    if (filterValue === reportViews.agencies) {
        sendEvent(
            ...(EVENT_ARGS(matomoTrackingObj).reports.agencyModalSelected as [string, string, any?]),
            selectedValueCount
        );
    } else if (filterValue === reportViews.agents) {
        sendEvent(
            ...(EVENT_ARGS(matomoTrackingObj).reports.agentsModalSelected as [string, string, any?]),
            selectedValueCount
        );
    } else if (filterValue === reportViews.locations) {
        sendEvent(
            ...(EVENT_ARGS(matomoTrackingObj).reports.locationModalSelected as [string, string, any?]),
            selectedValueCount
        );
    }
};

const _trackFilterUpdates = (payload: any): void => {
    const filterId = get(payload, 'filterId');
    const selectedValueCount = get(payload, 'value.subvalues', []).length;
    const selectedSubValue = get(payload, 'value.subvalues', []);
    const selectedValue = get(payload, 'value.value');
    const selectedFilterValue = get(payload, 'value');

    if (filterId === filterIds.reportViewFilter) {
        _trackReportViewFilters(selectedValue, selectedValueCount, selectedSubValue);
    } else if (filterId === filterIds.datesFilter) {
        sendEvent(...(EVENT_ARGS(selectedFilterValue).reports.dateFilterChanged as [string, string, any?]));
    } else if (filterId === filterIds.reportGranularityFilter) {
        sendEvent(...(EVENT_ARGS(selectedFilterValue).reports.granularityChanged as [string, string, any?]));
    } else if (filterId === filterIds.dashboardCardFilter) {
        sendEvent(...(EVENT_ARGS(selectedFilterValue).dashboard.dashboardCardFilterChanged as [string, string, any?]));
    } else if (filterId === filterIds.agentsCommonDashboardFilter) {
        sendEvent(...(EVENT_ARGS(selectedFilterValue).dashboard.traineeFilter as [string, string, any?]));
    } else {
        sendEvent(...(EVENT_ARGS({ filter: filterId, selectedFilterValue }).untrackedEvent as [string, string, any?]));
    }
};

export const eventsTracker: Middleware =
    (store: MiddlewareAPI<Dispatch<AnyAction>, RootState>) => (next: Dispatch<AnyAction>) => (action: AnyAction) => {
        const { type, payload, request } = action;

        if (type === SET_CARD_FILTER && payload.key === 'granularity') {
            sendEvent(...(EVENT_ARGS(payload).dashboard.granularityChanged as [string, string, any?]), {
                [GA_DIMENSIONS.cardId]: payload.cardId
            });
        } else if (type === SEND_REQUEST_SUCCESS && request.type === REQUEST_TYPES.GET_REPORT) {
            _trackReportType(get(request, 'params.reportType'), get(request, 'requestId'));
        } else if (type === UPDATE_FILTER) {
            _trackFilterUpdates(payload);
        } else if (type === SET_DATA_ROOM_MODE) {
            sendEvent(
                ...(EVENT_ARGS({
                    is_recorded_calls: payload.isDataRoomEnabled
                }).reports.recordedCallsOnlyFilter as [string, string, any?])
            );
        }

        return next(action);
    };
